import { mockSeriesOverview } from './api-data';
import type { Report } from '../store';

/**
 * Creates a mock report that can be used when API calls fail
 * This ensures we always have something to display
 */
export function createMockReport(slug: string): Report {
  // Safety check - if mock data is not loaded properly, create a minimal valid report
  if (
    !mockSeriesOverview ||
    !mockSeriesOverview.items ||
    !mockSeriesOverview.items.length
  ) {
    console.warn('Mock data not available, creating basic report');
    return {
      slug: slug,
      title: `Sample Report (${slug})`,
      cover: {
        uri: '',
        name: 'Sample Cover',
        extension: '.jpg',
        mime_type: 'image/jpeg',
        alt: 'Sample Cover Image',
        width: 300,
        height: 400,
      },
      subject: {
        slug: 'sample',
        name: 'Sample',
        title: 'Sample Subject',
      },
      format: {
        slug: 'sample-format',
        name: 'Sample Format',
        type: 'Sample',
        subtype: 'Sample Subtype',
        icon: {
          uri: '',
          name: 'Sample Icon',
          extension: '.svg',
          mime_type: 'image/svg+xml',
          alt: 'Sample Icon',
          width: 26,
          height: 26,
        },
      },
      grades: [
        {
          slug: 'sample-grade',
          name: 'Sample',
          title: 'Sample Grade',
          order: 0,
        },
      ],
      release: Date.now() / 1000,
      version: '1.0',
      gateways: [
        {
          slug: 'gateway-1',
          label: 'Gateway 1',
          name: 'Sample Gateway',
          title: 'Sample Gateway Title',
          score: '100%',
          status: {
            slug: 'meets',
            name: 'Meets',
            title: 'Meets Expectations',
          },
          order: 0,
          failed_non_negotiable: false,
        },
      ],
      details: [
        {
          label: 'Description',
          value: 'This is a sample report created for preview purposes.',
        },
        {
          label: 'Note',
          value: 'This is sample data used because the API is unavailable.',
        },
      ],
      groups: [],
      alerts: [],
      copyright: '© Sample Copyright',
    };
  }

  // First try to find a report with the requested slug
  const matchingReport = mockSeriesOverview.items.find(
    (item) => item.slug === slug,
  );

  if (matchingReport) {
    // Return a deep copy of the matching report
    return JSON.parse(JSON.stringify(matchingReport));
  }

  // If no matching report found, use the first item and modify it
  const baseMockReport = mockSeriesOverview.items[0];
  const mockReport = JSON.parse(JSON.stringify(baseMockReport));

  // Update with the requested slug
  mockReport.slug = slug;
  mockReport.title = `Sample Report (${slug})`;

  // Add a note to the details
  if (!mockReport.details) {
    mockReport.details = [];
  }

  mockReport.details.push({
    label: 'Note',
    value: 'This is sample data used because the API is unavailable.',
  });

  return mockReport;
}

/**
 * Fetches the report data directly from the API with fallbacks
 */
export async function fetchReportFromApi(slug: string, API_BASE_URL: string): Promise<{
  report: Report | null;
  error: string | null;
  usingMockData: boolean;
}> {
  let report: Report | null = null;
  let error: string | null = null;
  let usingMockData = false;

  try {
    // First attempt: Use direct fetch with error handling
    try {
      const directUrl = `${API_BASE_URL}/reports/${slug}`;

      const fetchResponse = await fetch(directUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slug }),
      });

      // Handle non-successful responses
      if (!fetchResponse.ok) {
        const errorText = await fetchResponse.text();
        console.error(`API Error (${fetchResponse.status}):`, errorText);
        throw new Error(
          `API Error (${fetchResponse.status}): ${errorText || 'No response'}`,
        );
      }

      // Get the response text and check if it's empty
      const responseText = await fetchResponse.text();
      if (!responseText || responseText.trim() === '') {
        console.error('Empty response from API');
        throw new Error('Empty response from API');
      }

      // Try to parse the JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError: any) {
        console.error('JSON parse error:', parseError);
        console.error('Response that caused error:', responseText);
        throw new Error(
          `JSON parse error: ${parseError.message || 'Unknown parsing error'}`,
        );
      }

      // Process the data
      if (data) {
        if (data.data) {
          report = data.data;
          return { report, error, usingMockData };
        } else if (data.slug && data.title) {
          report = data;
          return { report, error, usingMockData };
        }
        // If we got here, the format is unexpected
        throw new Error('Unexpected data format in API response');
      } else {
        throw new Error('No data in API response');
      }
    } catch (fetchError) {
      console.error('Direct fetch attempt failed:', fetchError);
      throw fetchError;
    }
  } catch (err) {
    error = err instanceof Error ? err.message : 'Failed to fetch report';
    console.error('Error in fetchReportFromApi:', err);

    // Final fallback: Use mock data
    try {
      // Generate a mock report based on the slug
      report = createMockReport(slug);
      usingMockData = true;

      // Show an info message instead of an error
      error = 'Using sample data (API unavailable)';
    } catch (mockError) {
      console.error('Error creating mock report:', mockError);
      error = 'Failed to create mock report';
    }
  }

  return { report, error, usingMockData };
} 